import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, tokens } from '../api/client';
import { useActiveSessions, useCurrentUser, usePayment, useSession } from '../api/hooks';
import type { SessionView } from '../api/types';
import { OfflineBanner, TopBar } from '../components/chrome';
import { IconCheck } from '../components/icons';
import { ReminderStatus } from '../components/reminder';
import {
  Card,
  Cta,
  ErrorState,
  LinkButton,
  Loading,
  Muted,
  Note,
  Row,
  Screen,
  SecondaryButton,
} from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { countdown, durationLabel, usd } from '../lib/format';
import { lotClockLabel, lotExpiryLabel, resolveLotTimeLanguage } from '../lib/lot-time';
import { useCountdown } from '../lib/use-countdown';

export function SuccessPage() {
  const { t, i18n } = useTranslation();
  const { sessionId, paymentId, kind } = useSearch({ from: '/success' }) as {
    sessionId: string;
    paymentId?: string;
    kind: 'new' | 'extension';
  };
  const navigate = useNavigate();
  const session = useSession(sessionId || undefined);
  const currentUser = useCurrentUser(tokens.signedIn);
  // The tender split lives on the payment, not the session — `paidTotalCents`
  // is deliberately the full price of the stay whatever paid for it.
  const payment = usePayment(paymentId);
  // The payment record is the authority on initial-vs-extension. The URL
  // param (quote.kind threaded via /pay and the Stripe return_url, see
  // app.tsx) remains only as a fallback: the deployed API may predate
  // Payment.kind (backend bc82c4c), and a session mid-bank-redirect carries
  // only its return_url. Retire the URL threading once every deployed backend
  // serves `kind` and in-flight redirects have drained. Amounts must never
  // decide the variant: a first payment and an extension can cost the same.
  const isExtension = payment.data?.kind ? payment.data.kind === 'extension' : kind === 'extension';
  const title = t(isExtension ? 'success.extTitle' : 'success.title');
  const lang = resolveLotTimeLanguage(i18n.language);

  // The extension variant's headline figure is THIS charge, which only the
  // payment record knows — rendering before it arrives would leave the one
  // number P0-4 exists for missing from the screenshot moment.
  if (session.isPending || !session.data || (paymentId !== undefined && payment.isPending)) {
    return (
      <>
        <TopBar title={title} back={false} />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  const data = session.data;

  return (
    <>
      <TopBar title={title} back={false} />
      <Screen>
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-success text-white">
          <IconCheck className="h-8 w-8" />
        </span>
        {/* The top bar already carries the success headline; the body keeps
            only the subtitle (review §5, no repeated titles). */}
        <Muted>{t(isExtension ? 'success.extSubtitle' : 'success.subtitle')}</Muted>

        <Card>
          {isExtension ? (
            <>
              {/* THIS charge is the extension payment's own amount; the session
                  total below is cumulative. Conflating them is the P0-4 defect,
                  so the two rows never share a label or a source field. */}
              {payment.data ? (
                <Row label={t('success.extCharged')} value={usd(payment.data.amountCents)} mono />
              ) : null}
              <Row label={t('success.extTotalPaid')} value={usd(data.paidTotalCents)} mono />
              <Row
                label={t('success.extEndsAt')}
                value={lotExpiryLabel(data.expiresAt, data.lot.timezone, lang, t)}
                mono
              />
              <Row label={t('success.plate')} value={data.plate} mono />
              <Row label={t('success.lot')} value={data.lot.name} />
            </>
          ) : (
            <>
              <Row
                label={t('success.endsAt')}
                value={lotExpiryLabel(data.expiresAt, data.lot.timezone, lang, t)}
                mono
              />
              <Row label={t('success.plate')} value={data.plate} mono />
              <Row label={t('success.lot')} value={data.lot.name} />
              {/* A first payment is the session's only payment, so the session
                  total IS this charge when the payment record is unavailable. */}
              <Row
                label={t('success.thisPayment')}
                value={usd(payment.data?.amountCents ?? data.paidTotalCents)}
                mono
              />
            </>
          )}
          {data.spaceNumber !== null ? (
            <Row label={t('success.spaceNumber')} value={data.spaceNumber.toString()} mono />
          ) : null}
          {/* A driver whose card was charged $0 must see that, not just the
              total the stay cost (issue 05). Server amounts, never recomputed. */}
          {payment.data && payment.data.creditAppliedCents > 0 ? (
            <>
              <Row label={t('pay.creditApplied')} value={usd(payment.data.creditAppliedCents)} mono />
              <Row
                label={t('pay.cardCharged')}
                value={usd(payment.data.amountCents - payment.data.creditAppliedCents)}
                mono
              />
            </>
          ) : null}
        </Card>

        {/* The primary post-payment task sits directly under the summary on
            both variants; everything optional stays below it (issue 11). */}
        <Cta
          label={t('success.viewSession')}
          onClick={() =>
            void navigate({ to: '/sessions/$sessionId', params: { sessionId: data.id }, replace: true })
          }
        />

        {currentUser.isSuccess && !currentUser.data.email && !currentUser.data.phoneE164 ? (
          <BindEmail />
        ) : null}

        {/* No unconditional reminder promise: the block below reflects the
            device's actual notification state (issue 06). */}
        <ReminderStatus session={data} />
      </Screen>
    </>
  );
}

/**
 * Optional email binding, offered only after the money has already moved.
 *
 * Paying required no account, which means this stay currently lives in one
 * browser's storage — clear it and the running session is gone. That is the
 * honest reason to offer this here, and the copy says so rather than dressing
 * it up as a marketing opt-in.
 *
 * Until expanded it must stay a single line below the primary CTA — a large
 * card here outranked the driver's actual next task (issue 11). The form only
 * grows to a card after the driver opts in.
 */
function BindEmail() {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [phase, setPhase] = useState<'idle' | 'email' | 'code' | 'done'>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'done') {
    return (
      <p className="px-1 text-center text-[13px] leading-relaxed text-muted">
        <span className="font-semibold text-success">{t('success.bound', { email })}</span>{' '}
        {t('success.boundBody')}
      </p>
    );
  }

  if (phase === 'idle') {
    return (
      <p className="px-1 text-center text-[13px] leading-relaxed text-muted">
        {t('success.bindLine')}{' '}
        <button
          type="button"
          onClick={() => setPhase('email')}
          className="font-semibold text-accent underline underline-offset-2"
        >
          {t('success.bindCta')}
        </button>
      </p>
    );
  }

  return (
    <Card>
      <p className="text-[15px] font-semibold">{t('success.bindHeading')}</p>
      {phase === 'email' ? (
        <>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            aria-label={t('login.emailLabel')}
            className="min-h-[52px] rounded-lg border-[1.5px] border-ink px-4 text-lg outline-none"
          />
          {/* Accent, not black: binding is a contextual side task — the
              screen's single black primary stays "view session" (review §5). */}
          <Cta
            tone="accent"
            label={t('success.sendCode')}
            loading={busy}
            disabled={!email.includes('@')}
            onClick={() =>
              void run(async () => {
                await api.post('/auth/link-email', { email });
                setPhase('code');
              })
            }
          />
        </>
      ) : (
        <>
          <Muted>{t('success.codeSent', { email })}</Muted>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder={t('login.codePlaceholder')}
            aria-label={t('login.codePlaceholder')}
            className="num min-h-[52px] rounded-lg border-[1.5px] border-ink px-4 text-xl tracking-[6px] outline-none"
          />
          <Cta
            tone="accent"
            label={t('success.finishBind')}
            loading={busy}
            disabled={code.length !== 6}
            onClick={() =>
              void run(async () => {
                await api.post('/auth/link-email/verify', { email, code });
                setPhase('done');
              })
            }
          />
        </>
      )}
      {error ? <p className="text-[13px] text-danger">{error}</p> : null}
      <LinkButton label={t('success.later')} onClick={() => setPhase('idle')} />
    </Card>
  );
}

/**
 * The in-progress screen: a countdown readable across a car park, a full-width
 * extend button, and every other running session one tap away — a family with
 * two cars will have several at once (FR-U-32 ~ 35).
 */
export function SessionPage() {
  const { t, i18n } = useTranslation();
  const { sessionId } = useParams({ from: '/sessions/$sessionId' });
  const navigate = useNavigate();
  const session = useSession(sessionId);
  const all = useActiveSessions();

  if (session.isPending) {
    return (
      <>
        <TopBar title={t('session.title')} />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  if (session.isError || !session.data) {
    return (
      <>
        <TopBar title={t('session.title')} />
        <Screen>
          <ErrorState message={t('session.loadFailed')} onRetry={() => void session.refetch()} />
        </Screen>
      </>
    );
  }

  const data = session.data;
  const others = (all.data?.sessions ?? []).filter((s) => s.id !== data.id);

  return (
    <>
      <TopBar title={t('session.title')} />
      <OfflineBanner />
      {/* Deliberately NOT wide: the active session stays a compact 520px
          single column at every width (review §4.13 exempts it) — it is a
          glanceable ticket, not a desktop dashboard. */}
      <Screen>
        <SessionCard
          session={data}
          onExtend={() => void navigate({ to: '/sessions/$sessionId/extend', params: { sessionId } })}
        />

        {data.status !== 'active' ? <Note>{t('session.endedNote')}</Note> : null}

        <ReminderStatus session={data} />

        {others.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <Muted>{t('session.otherPlates')}</Muted>
              <span className="num text-[11px] text-faint">
                {t('session.sessionCount', { count: others.length + 1 })}
              </span>
            </div>
            {others.map((other) => (
              <div
                key={other.id}
                className="flex items-center gap-3 rounded-lg border-[1.5px] border-hairline p-3"
              >
                <Link
                  to="/sessions/$sessionId"
                  params={{ sessionId: other.id }}
                  className="flex flex-1 flex-col gap-1"
                >
                  <span className="num text-lg font-semibold tracking-[1.5px]">{other.plate}</span>
                  <span className="text-xs text-muted">
                    {other.lot.name} ·{' '}
                    {lotClockLabel(
                      other.expiresAt,
                      other.lot.timezone,
                      resolveLotTimeLanguage(i18n.language),
                    )}
                  </span>
                </Link>
                <span className="num text-[17px]">{countdown(other.remainingSeconds)}</span>
                <button
                  type="button"
                  onClick={() =>
                    void navigate({ to: '/sessions/$sessionId/extend', params: { sessionId: other.id } })
                  }
                  className="rounded-lg bg-accent px-3 text-[13px] font-semibold text-white"
                >
                  {t('session.extend')}
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <SecondaryButton dashed label={t('session.payAnother')} onClick={() => void navigate({ to: '/' })} />
        <Link
          to="/history"
          className="flex min-h-11 items-center justify-center text-center text-sm text-accent"
        >
          {t('common.history')}
        </Link>
      </Screen>
    </>
  );
}

function SessionCard({ session, onExtend }: { session: SessionView; onExtend: () => void }) {
  const { t, i18n } = useTranslation();
  const remaining = useCountdown(session.expiresAt, session.remainingSeconds);
  const soon = remaining > 0 && remaining <= 15 * 60;

  return (
    // rounded-2xl: the active-session card is the one container allowed a
    // stronger radius than the 8px system (review §5).
    <div className="flex flex-col gap-4 rounded-2xl bg-ink p-6 text-white">
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-wider opacity-65">
          {remaining > 0 ? t('session.inProgress') : t('session.ended')}
        </span>
        <span className="num text-xs opacity-65">{session.plate}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <span
          className="num text-[56px] font-semibold leading-none tracking-[-2px]"
          style={soon ? { color: '#FFB27A' } : undefined}
        >
          {countdown(remaining)}
        </span>
        <span className="text-[13px] opacity-70">
          {t('session.remaining', {
            time: lotClockLabel(
              session.expiresAt,
              session.lot.timezone,
              resolveLotTimeLanguage(i18n.language),
            ),
          })}
        </span>
      </div>

      <div className="flex flex-col gap-2 border-t border-white/20 pt-3.5">
        {session.spaceNumber !== null ? (
          <div className="flex justify-between text-sm">
            <span className="opacity-65">{t('session.spaceNumber')}</span>
            <span className="num">{session.spaceNumber}</span>
          </div>
        ) : null}
        <div className="flex justify-between text-sm">
          <span className="opacity-65">{session.lot.name}</span>
          <span>{durationLabel(session.totalMinutes, t)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="opacity-65">{t('session.paidTotal')}</span>
          <span className="num">{usd(session.paidTotalCents)}</span>
        </div>
      </div>

      <Cta
        label={t('session.extend')}
        note={soon ? t('session.extendSoon') : t('session.extendNote')}
        tone="accent"
        onClick={onExtend}
      />
    </div>
  );
}
